import React, {useContext, useState} from "react";
import './Users.scss'
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import {buttonColors} from "../Pickers/InputTheme";
import {ImportUsersModal} from "./ImportUsersModal/ImportUsersModal";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import {GridReactContext, GridReactContextProvider} from "../../Utils/DataGridWrapper/GridContext";
import OrganizationUsersTable from "./OrganizationUsersTable/OrganizationUsersTable";
import {CreateEditUserModal} from "./CreateEditUserModal/CreateEditUserModal";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {useUserAdminOrgs} from "../Hooks/UseUserAdminOrgs";
import {ImportUserContextProvider} from "./ImportUsersModal/ImportUsersModalContext";
import {getSignDocsOrganizationUsersCSVApi, bulkExportUsersAPI, downloadUsersDetailsAsCSV} from "../../Services/api";
import {useNavigate} from "react-router-dom";
import {I18nReactContext} from "../../contexts/I18nContext";
import {DeleteUsersTableButton} from "../BulkDeleteUsers/DeleteUsersTableButton";
import {get} from "lodash";
import {addAllSignDocTypes, prepareFullReportFilter} from "../../Utils/filterUtils";

export const Users = () => {
    const {
        adminOrganizationOptions,
        triggerReloadOrganizations
    } = useUserAdminOrgs({withAllOption: true, withCoursesAndSignDocs: false});
    const {i18n} = useContext(I18nReactContext);
    const navigate = useNavigate();
    const [importUsersModalOpen, setImportUsersModalOpen] = useState(false)
    const [createEditUserModalOpen, setCreateEditUserModalOpen] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)
    const gridContext = React.useContext(GridReactContext)
    const openImportUsersModal = () => {
        setImportUsersModalOpen(true)
    }
    const closeImportUsersModal = () => {
        setImportUsersModalOpen(false)
    }
    const openCreateEditUserModal = () => {
        setCreateEditUserModalOpen(true)
    }
    const closeCreateEditUserModal = () => {
        setCreateEditUserModalOpen(false)
    }
    const onSuccessCreateEditUser = () => {
        closeCreateEditUserModal()
        gridContext.triggerTableRefresh()
    }

    const onSuccessCreateUsers = async () => {
        closeImportUsersModal()
        await triggerReloadOrganizations()
        gridContext.triggerTableRefresh()
    }

    const onDeleteUsersSuccess = () => {
        // Refresh table after successful deletion
        if (gridContext && gridContext.triggerTableRefresh) {
            gridContext.triggerTableRefresh();
        }
    };

    const buttons = [
        //download csv button
        // {
        //     type: "A-Button",
        //     icon: <CloudDownloadIcon/>,
        //     href: `${process.env.REACT_APP_PUBLIC_FILES_URL}/weezbee-csv-example.csv`,
        //     text: i18n.translate('importUsersPage.downloadExample')
        // },
        {
            icon: <GroupAddOutlinedIcon/>,
            color: buttonColors.GREY,
            text: i18n.translate('importUsersModal.title'),
            onClick: () => {
                openImportUsersModal()
            }
        },
        {
            icon: <PersonAddIcon/>,
            color: buttonColors.GREY,
            text: i18n.translate('createEditUserModal.createUser'),
            onClick: () => {
                openCreateEditUserModal()
            }
        },
        {
            type: 'ComponentButton',
            component: ({paginationModel, filterModel, sortModel, selectionModel}) => (
                <DeleteUsersTableButton
                    onSuccess={onDeleteUsersSuccess}
                    selectionModel={selectionModel}
                    paginationModel={paginationModel}
                    filterModel={filterModel}
                    sortModel={sortModel}
                />
            )
        },
        {
            type: 'MultipleOptionsButton',
            'data-testid': 'download-button',
            text: i18n.translate('LearningManagementPage.users.buttons.downloadCsv'),
            icon: <CloudDownloadIcon/>,
            loading: isDownloading,
            disabled: isDownloading,
            options: [
                {
                    'data-testid': 'download-csv-template-option',
                    text: i18n.translate('importUsersPage.downloadExample'),
                    type: "A-Button",
                    href: `${process.env.REACT_APP_PUBLIC_FILES_URL}/weezbee-csv-example-v2.csv`,
                },
                {
                    text: i18n.translate('LearningManagementPage.users.buttons.downloadUserDetailsForOrganizations'),
                    'data-testid': 'download-user-details-option',
                    disabled: isDownloading,
                    action: async ({filterModel}) => {
                        setIsDownloading(true)
                        try {
                            const selectedOrgIds = get(filterModel, 'filterValues.organizationIds', [])
                            // If "all" is selected, pass empty array to get all organizations
                            const organizationIds = selectedOrgIds.includes('all') ? [] : selectedOrgIds
                            await downloadUsersDetailsAsCSV({organizationIds})
                            navigate('/reports/generatedReports')
                        } catch (error) {
                            console.error('Error downloading user details:', error)
                        } finally {
                            setIsDownloading(false)
                        }
                    }
                },
                {
                    text: i18n.translate('LearningManagementPage.users.buttons.downloadCsvForOrganizations'),
                    'data-testid': 'download-organization-option',
                    disabled: isDownloading,
                    action: async ({paginationModel, filterModel, sortModel}) => {
                        setIsDownloading(true)
                        try {
                            // Add all SignDoc types to the filter using utility
                            const filterWithTypes = addAllSignDocTypes(filterModel);

                            await getSignDocsOrganizationUsersCSVApi({
                                paginationModel,
                                filterModel: filterWithTypes,
                                sortModel
                            });
                            navigate('/reports/generatedReports')
                        } catch (error) {
                            console.error('Error downloading users report:', error)
                        } finally {
                            setIsDownloading(false)
                        }
                    }
                },
                {
                    'data-testid': 'download-selected-users-option',
                    text: i18n.translate('LearningManagementPage.users.buttons.downloadSelectedUsers'),
                    disabled: ({selectionModel}) => isDownloading || !selectionModel || selectionModel.length === 0,
                    shouldShow: ({selectionModel}) => selectionModel && selectionModel.length > 0,
                    action: async ({selectionModel, filterModel}) => {
                        setIsDownloading(true)
                        try {
                            // Get organization IDs from filter
                            const organizationIds = get(
                                filterModel,
                                'filterValues.organizationIds',
                                ['all']
                            );

                            // Call export API - WAITS for completion (synchronous)
                            await bulkExportUsersAPI({
                                userIds: selectionModel,
                                organizationIds,
                                includeDeleted: false
                            });
                            navigate('/reports/generatedReports')
                        } catch (error) {
                            console.error('Error downloading selected users:', error)
                        } finally {
                            setIsDownloading(false)
                        }
                    }
                },
            ]
        },
    ]

    return <div className="UsersPage">
        <GridReactContextProvider>
            <div className="TableWRapper">
                {adminOrganizationOptions ?
                    <OrganizationUsersTable tableButtons={buttons} organizationsOptions={adminOrganizationOptions}/> :
                    <LoadingSpinner/>}
            </div>
            <ImportUserContextProvider>
                {importUsersModalOpen && <ImportUsersModal
                    onlyImport
                    closeImportUsersModal={closeImportUsersModal}
                    importUserModalOpen={importUsersModalOpen}
                    onSuccessCreateUsers={onSuccessCreateUsers}/>}
            </ImportUserContextProvider>
            {createEditUserModalOpen && <CreateEditUserModal closeCreateEditUserModal={closeCreateEditUserModal}
                                                             createEditUserModalOpen={createEditUserModalOpen}
                                                             onSuccess={onSuccessCreateEditUser}
                                                             editMode={false}
                                                             userId={null}/>}
        </GridReactContextProvider>
    </div>
}